import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, AlertCircle, Mail, Trash2, Shield, ArrowLeft, Loader2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function UnsubscribePage() {
  const { token } = useParams();
  const [status, setStatus] = useState("confirm");
  const [loading, setLoading] = useState(false);

  const handleUnsubscribe = async () => {
    setLoading(true);
    try {
      await axios.delete(`${API}/newsletter/abmelden/${token}`);
      setStatus("success");
    } catch (err) {
      setStatus(err.response?.status === 404 ? "not_found" : "error");
    }
    setLoading(false);
  };

  return (
    <PageWrapper>
      {status === "confirm" && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-red-50 flex items-center justify-center">
              <Trash2 className="w-8 h-8 text-red-400" />
            </div>
          </div>
          <h1 className="font-playfair text-2xl font-semibold text-lab-primary mb-3">Newsletter abmelden?</h1>
          <p className="text-lab-text-muted mb-8">Sie werden keine weiteren Newsletter von LAB336 erhalten. Ihre Daten werden gelöscht.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              onClick={handleUnsubscribe}
              disabled={loading}
              className="px-8 py-3 bg-red-600 text-white text-sm font-semibold hover:bg-red-700 transition-colors disabled:opacity-50"
              data-testid="confirm-unsubscribe"
            >
              {loading ? "Wird verarbeitet..." : "Ja, abmelden"}
            </button>
            <Link to="/" className="px-8 py-3 border border-lab-border text-lab-primary text-sm font-semibold hover:bg-lab-bg-light transition-colors">
              Abbrechen
            </Link>
          </div>
        </motion.div>
      )}
      {status === "success" && (
        <ResultCard icon={<CheckCircle className="w-12 h-12 text-green-500" />} title="Erfolgreich abgemeldet" description="Sie erhalten keine weiteren Newsletter von LAB336. Wir bedauern, Sie gehen zu sehen." />
      )}
      {status === "not_found" && (
        <ResultCard icon={<AlertCircle className="w-12 h-12 text-amber-500" />} title="Bereits abgemeldet" description="Dieses Abonnement existiert nicht mehr oder wurde bereits gekündigt." />
      )}
      {status === "error" && (
        <ResultCard icon={<AlertCircle className="w-12 h-12 text-red-500" />} title="Fehler" description="Die Abmeldung konnte nicht verarbeitet werden. Bitte versuchen Sie es später erneut." />
      )}
    </PageWrapper>
  );
}

function ChangeEmailPage() {
  const { token } = useParams();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("form");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.put(`${API}/newsletter/email-aendern/${token}`, { email });
      setStatus("success");
    } catch (err) {
      setStatus(err.response?.status === 409 ? "duplicate" : err.response?.status === 404 ? "not_found" : "error");
    }
    setLoading(false);
  };

  return (
    <PageWrapper>
      {status === "form" && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-md mx-auto">
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 bg-lab-accent/10 flex items-center justify-center">
              <Mail className="w-8 h-8 text-lab-accent" />
            </div>
          </div>
          <h1 className="font-playfair text-2xl font-semibold text-lab-primary text-center mb-2">E-Mail-Adresse ändern</h1>
          <p className="text-lab-text-muted text-center mb-8">Geben Sie Ihre neue E-Mail-Adresse ein.</p>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="change-email-form">
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder="Neue E-Mail-Adresse" className="w-full px-4 py-3 border border-lab-border text-sm focus:outline-none focus:border-lab-accent" data-testid="change-email-input" />
            <button type="submit" disabled={loading} className="w-full bg-lab-primary text-white py-3 text-sm font-semibold hover:bg-lab-primary/90 transition-colors disabled:opacity-50" data-testid="change-email-submit">
              {loading ? "Wird gespeichert..." : "E-Mail ändern"}
            </button>
          </form>
        </motion.div>
      )}
      {status === "success" && <ResultCard icon={<CheckCircle className="w-12 h-12 text-green-500" />} title="E-Mail geändert" description={`Ihre Newsletter werden ab sofort an ${email} gesendet.`} />}
      {status === "duplicate" && <ResultCard icon={<AlertCircle className="w-12 h-12 text-amber-500" />} title="E-Mail bereits registriert" description="Diese Adresse ist bereits für den Newsletter angemeldet." />}
      {status === "not_found" && <ResultCard icon={<AlertCircle className="w-12 h-12 text-amber-500" />} title="Nicht gefunden" description="Dieses Abonnement existiert nicht mehr." />}
      {status === "error" && <ResultCard icon={<AlertCircle className="w-12 h-12 text-red-500" />} title="Fehler" description="Die Änderung konnte nicht gespeichert werden." />}
    </PageWrapper>
  );
}

function SelfDisclosurePage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    axios.get(`${API}/newsletter/subscriber/${token}`)
      .then(r => { setData(r.data); setStatus("success"); })
      .catch(() => setStatus("not_found"));
  }, [token]);

  return (
    <PageWrapper>
      {status === "loading" && <LoadingState text="Daten werden geladen..." />}
      {status === "not_found" && <ResultCard icon={<AlertCircle className="w-12 h-12 text-amber-500" />} title="Nicht gefunden" description="Kein Abonnement mit diesem Token gefunden." />}
      {status === "success" && data && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-lg mx-auto">
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 bg-lab-accent/10 flex items-center justify-center">
              <Shield className="w-8 h-8 text-lab-accent" />
            </div>
          </div>
          <h1 className="font-playfair text-2xl font-semibold text-lab-primary text-center mb-2">Selbstauskunft</h1>
          <p className="text-lab-text-muted text-center mb-8">Gemäss nDSG Art. 25 haben Sie das Recht auf Auskunft über Ihre gespeicherten Daten.</p>
          
          <div className="border border-lab-border bg-white" data-testid="self-disclosure-data">
            <div className="px-6 py-4 border-b border-lab-border bg-lab-bg-light">
              <h2 className="text-sm font-semibold text-lab-primary uppercase tracking-wider">Gespeicherte Daten</h2>
            </div>
            <div className="divide-y divide-lab-border">
              <DataRow label="E-Mail" value={data.email} />
              <DataRow label="Anmeldedatum" value={new Date(data.timestamp).toLocaleDateString("de-CH", { day: "numeric", month: "long", year: "numeric" })} />
              <DataRow label="Abonnenten-ID" value={data.id} />
              <DataRow label="Datenquelle" value="Newsletter-Anmeldung auf lab336.ch" />
              <DataRow label="Verwendungszweck" value="Versand des LAB336 Newsletters" />
              <DataRow label="Speicherdauer" value="Bis zur Abmeldung" />
            </div>
          </div>

          <div className="mt-6 p-4 bg-lab-bg-light border border-lab-border text-center">
            <p className="text-xs text-lab-text-muted mb-3">Möchten Sie Ihre Daten löschen lassen?</p>
            <Link to={`/newsletter/abmelden/${token}`} className="text-sm text-red-600 hover:underline inline-flex items-center gap-1">
              <Trash2 className="w-3 h-3" /> Abmelden und Daten löschen
            </Link>
          </div>
        </motion.div>
      )}
    </PageWrapper>
  );
}

// ── Shared Components ──

function PageWrapper({ children }) {
  return (
    <div className="min-h-screen bg-lab-bg-light">
      <header className="bg-lab-bg-dark py-6 px-6">
        <div className="max-w-xl mx-auto">
          <Link to="/" className="font-playfair text-xl font-semibold text-white">LAB336</Link>
        </div>
      </header>
      <main className="max-w-xl mx-auto px-6 py-16">{children}</main>
      <footer className="bg-lab-bg-dark py-6 px-6 mt-auto">
        <div className="max-w-xl mx-auto flex items-center justify-between">
          <p className="text-gray-500 text-xs">&copy; {new Date().getFullYear()} LAB336</p>
          <Link to="/" className="text-gray-400 hover:text-lab-accent text-xs">Startseite</Link>
        </div>
      </footer>
    </div>
  );
}

function ResultCard({ icon, title, description }) {
  return (
    <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center">
      <div className="flex justify-center mb-6">{icon}</div>
      <h1 className="font-playfair text-2xl font-semibold text-lab-primary mb-3">{title}</h1>
      <p className="text-lab-text-muted mb-8">{description}</p>
      <Link to="/" className="inline-flex items-center gap-2 text-lab-accent hover:underline text-sm">
        <ArrowLeft className="w-4 h-4" /> Zurück zur Startseite
      </Link>
    </motion.div>
  );
}

function LoadingState({ text }) {
  return (
    <div className="text-center py-12">
      <Loader2 className="w-8 h-8 text-lab-accent animate-spin mx-auto mb-4" />
      <p className="text-lab-text-muted">{text}</p>
    </div>
  );
}

function DataRow({ label, value }) {
  return (
    <div className="px-6 py-3 flex justify-between items-start gap-4">
      <span className="text-sm text-lab-text-muted flex-shrink-0">{label}</span>
      <span className="text-sm text-lab-primary text-right">{value}</span>
    </div>
  );
}

export { UnsubscribePage, ChangeEmailPage, SelfDisclosurePage };
